import {
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  Trash2,
  Clock,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DatePicker from '../DatePicker'
import type { Ct600PeriodSplitData, StoredCompany } from '../../types'
import type {
  CT600SetupForm,
  CT600Draft,
  CT600CompanyInfoForm,
} from '../../types/ct600Filing'
import { ct600DraftService } from '../../utils/ct600DraftService'

const returnTypes = [
  { label: 'Corporation Tax and Company Accounts', value: 'full' },
  { label: 'Corporation Tax only', value: 'tax-only' },
  { label: 'Company Accounts only', value: 'accounts-only' },
] as const

function formatDraftDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatPeriod(start: string, end: string): string {
  try {
    const fmt = (d: string) =>
      new Date(d).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    return `${fmt(start)} — ${fmt(end)}`
  } catch {
    return `${start} — ${end}`
  }
}

interface Props {
  companies: StoredCompany[]
  setup: CT600SetupForm
  onSetupChange: (update: Partial<CT600SetupForm>) => void
  onCompanySelect: (company: StoredCompany) => void
  onLoadDraft: (draft: CT600Draft) => void
  isLoadingCompanies: boolean
  validationErrors: Record<string, string>
  selectedPlanSlug: string | null
  onOpenPlanModal: () => void
  periodSplit: Ct600PeriodSplitData | null
  isLoadingPeriodSplit: boolean
  periodSplitError: string | null
  onRefreshPeriodSplit: () => void
  onCalculateManualSplit: () => void | Promise<void>
  isPeriodConfirmed: boolean
  isFirstReturn: boolean
  isFirstReturnAnswered: boolean
  onFirstReturnChange: (value: boolean) => void
  companyFlags: CT600CompanyInfoForm['flags']
  onCompanyFlagsChange: (flags: CT600CompanyInfoForm['flags']) => void
}

export default function CT600StepSetup({
  companies,
  setup,
  onSetupChange,
  onCompanySelect,
  onLoadDraft,
  isLoadingCompanies,
  validationErrors,
  selectedPlanSlug,
  onOpenPlanModal,
  periodSplit,
  isLoadingPeriodSplit,
  periodSplitError,
  onRefreshPeriodSplit,
  onCalculateManualSplit,
  isPeriodConfirmed,
  isFirstReturn,
  isFirstReturnAnswered,
  onFirstReturnChange,
  companyFlags,
  onCompanyFlagsChange,
}: Props) {
  // Convenience flags — drive the date-entry UX branches below.
  // - ct-only: fully manual dates + a "calculate split" step. Keeps the
  //   detailed HMRC guidance and the "first return" question.
  // - full (both): dates imported straight from Companies House — shows a short
  //   import note instead of the HMRC guidance, no "first return" question.
  // - accounts-only: pure Companies House dates — no wording, no question, no
  //   confirmation; the dates just auto-populate.
  const isCtOnly = setup.returnType === 'tax-only'
  const isFull = setup.returnType === 'full'
  const navigate = useNavigate()
  const [drafts, setDrafts] = useState<CT600Draft[]>([])
  const [showLpoaNotes, setShowLpoaNotes] = useState(false)

  const selectedCompany = companies.find(c => c._id === setup.companyId)
  const incorporationDate = selectedCompany?.incorporationDate

  // Alphabet B — answering the "first return" question. When the user says
  // YES we pre-populate the accounting period start with the company's
  // incorporation date (pulled from Companies House on "Add company"). When
  // they say NO we leave the dates open for them to enter manually.
  const handleFirstReturnSelect = (value: boolean) => {
    onFirstReturnChange(value)
    if (value && incorporationDate) {
      onSetupChange({ accountingPeriodStart: incorporationDate.slice(0, 10) })
    }
  }

  useEffect(() => {
    setDrafts(ct600DraftService.listDrafts())
  }, [])

  const handleDeleteDraft = (draftKey: string) => {
    ct600DraftService.deleteDraft(draftKey)
    setDrafts(ct600DraftService.listDrafts())
  }

  const handleLoadDraft = (draft: CT600Draft) => {
    onLoadDraft(draft)
    setDrafts([]) // hide the list after loading
  }

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>
          Company & Period Setup
        </h2>
        <p className='mt-1 text-sm text-gray-600'>
          Select the company you are filing for and set the accounting period
          details.
        </p>
      </div>

      {/* Saved Drafts */}
      {drafts.length > 0 && !setup.companyId && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <Clock className='h-4 w-4 text-gray-500' />
            <h3 className='text-sm font-semibold text-gray-900'>
              Resume a Saved Draft
            </h3>
          </div>
          <div className='space-y-2'>
            {drafts.map(draft => (
              <div
                key={draft.draftKey}
                className='flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 hover:border-gray-300 transition-colors'
              >
                <FileText className='h-5 w-5 flex-shrink-0 text-gray-400' />
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-gray-900 truncate'>
                    {draft.companyName || draft.companyNumber}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {formatPeriod(
                      draft.setup.accountingPeriodStart,
                      draft.setup.accountingPeriodEnd
                    )}
                    {' · '}Step {draft.currentStep} of 7{' · '}Saved{' '}
                    {formatDraftDate(draft.savedAt)}
                  </p>
                </div>
                <button
                  type='button'
                  onClick={() => handleLoadDraft(draft)}
                  className='rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 transition-colors'
                >
                  Resume
                </button>
                <button
                  type='button'
                  onClick={() => handleDeleteDraft(draft.draftKey)}
                  className='rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors'
                  title='Delete draft'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>
            ))}
          </div>
          <div className='border-t border-gray-200 pt-4'>
            <p className='text-xs text-gray-500'>Or start a new filing below</p>
          </div>
        </div>
      )}

      {/* Company Selection */}
      <div className='space-y-4'>
        <div className='flex items-center justify-between gap-3'>
          <h3 className='text-base font-semibold text-gray-900'>
            Select Company
          </h3>
          <button
            type='button'
            onClick={() => navigate('/add-company')}
            className='inline-flex items-center gap-1.5 text-sm font-semibold text-[#F04438] hover:text-[#DC2626] transition-colors cursor-pointer'
          >
            <Plus className='h-4 w-4' />
            Add a company
          </button>
        </div>
        {!isLoadingCompanies && companies.length === 0 && (
          <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800'>
            You don&apos;t have any companies yet.{' '}
            <button
              type='button'
              onClick={() => navigate('/add-company')}
              className='font-semibold underline hover:text-amber-900 cursor-pointer'
            >
              Add a company
            </button>{' '}
            to start your CT600 filing.
          </div>
        )}
        <div className='relative'>
          <Building2 className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
          <select
            value={setup.companyId}
            onChange={e => {
              const company = companies.find(c => c._id === e.target.value)
              if (company) onCompanySelect(company)
            }}
            className='w-full appearance-none rounded-xl border border-gray-300 bg-white py-3.5 pl-12 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
          >
            <option value=''>
              {isLoadingCompanies ? 'Loading companies...' : 'Select a company'}
            </option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>
                {company.companyName || company.name} ({company.companyNumber})
              </option>
            ))}
          </select>
          <ChevronDown className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
        </div>
        {validationErrors.companyId && (
          <p className='text-xs text-red-600'>{validationErrors.companyId}</p>
        )}

        {setup.companyId && (
          <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm'>
              <div>
                <span className='text-gray-500'>Company Name:</span>
                <span className='ml-2 font-medium text-gray-900'>
                  {setup.companyName}
                </span>
              </div>
              <div>
                <span className='text-gray-500'>Number:</span>
                <span className='ml-2 font-medium text-gray-900'>
                  {setup.companyNumber}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filing type & tax reference */}
      <div className='space-y-4'>
        <h3 className='text-base font-semibold text-gray-900'>
          Filing type & tax reference
        </h3>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
          <div>
            <label
              htmlFor='ct600-return-type'
              className='mb-2 block text-sm font-medium text-gray-900'
            >
              What are you filing?
            </label>
            <div className='relative'>
              <select
                id='ct600-return-type'
                value={setup.returnType}
                onChange={e =>
                  onSetupChange({
                    returnType: e.target.value as CT600SetupForm['returnType'],
                  })
                }
                className='w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 py-3.5 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
              >
                {returnTypes.map(rt => (
                  <option key={rt.value} value={rt.value}>
                    {rt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
            </div>
          </div>
          {/* UTR is only needed for Corporation Tax. Company-accounts-only
              filings don't go to HMRC for CT, so we hide it there. */}
          {setup.returnType !== 'accounts-only' && (
            <div>
              <label
                htmlFor='ct600-utr'
                className='mb-2 block text-sm font-medium text-gray-900'
              >
                Unique Taxpayer Reference (UTR)
              </label>
              <input
                id='ct600-utr'
                type='text'
                value={setup.utr}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                  onSetupChange({ utr: digits })
                }}
                placeholder='10-digit UTR'
                maxLength={10}
                className='w-full rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
              />
              {validationErrors.utr && (
                <p className='mt-1 text-xs text-red-600'>
                  {validationErrors.utr}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Accounting Period */}
      <div className='space-y-4'>
        {/* Alphabet A — detailed HMRC accounting period guidance. Only the
            manual Corporation-Tax-only flow needs this (the user must match the
            dates HMRC holds). "Both" imports from Companies House (see the note
            below) and "Company Accounts only" needs no wording at all. */}
        {isCtOnly && (
          <div className='rounded-xl border border-blue-200 bg-blue-50 p-5'>
            <div className='flex items-start gap-3'>
              <Info className='h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600' />
              <div className='space-y-3 text-sm text-blue-900'>
                <div>
                  <p className='font-semibold'>HMRC Accounting Period</p>
                  <p className='mt-1 text-blue-800'>
                    Enter the accounting period for this Corporation Tax return.
                    These dates must match the accounting period held by HMRC.
                  </p>
                </div>
                <div>
                  <p className='font-medium italic'>
                    First Corporation Tax return
                  </p>
                  <ul className='mt-1 list-disc space-y-1 pl-5 text-blue-800'>
                    <li>
                      Use your company&apos;s incorporation date as the
                      accounting period start.
                    </li>
                    <li>
                      For most companies, the accounting period will end on the
                      last day of the same month in the following year.
                    </li>
                    <li className='italic'>
                      Example: Incorporated on 5 August 2025. Accounting period
                      will be 5 August 2025 – 31 August 2026.
                    </li>
                  </ul>
                </div>
                <div>
                  <p className='font-medium italic'>Normal annual filing</p>
                  <ul className='mt-1 list-disc space-y-1 pl-5 text-blue-800'>
                    <li>
                      Your accounting period will usually start the day after
                      your previous accounting period ended and end 12 months
                      later.
                    </li>
                    <li className='italic'>
                      Example: Previous period 1 January 2025 – 31 December
                      2025. Current period will be 1 January 2026 – 31 December
                      2026.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Both filings together — dates are imported directly from Companies
            House, so we show a short note instead of the HMRC guidance. */}
        {isFull && (
          <div className='rounded-xl border border-blue-200 bg-blue-50 p-5'>
            <div className='flex items-start gap-3'>
              <Info className='h-5 w-5 mt-0.5 flex-shrink-0 text-blue-600' />
              <p className='text-sm text-blue-900'>
                We&apos;ve automatically imported your accounting period dates
                from Companies House for both your Company Accounts and
                Corporation Tax return. If the accounting periods are different,
                please submit each filing separately. You will only be charged
                once, even if the filings are submitted separately.
              </p>
            </div>
          </div>
        )}

        {/* Alphabet B — "first Corporation Tax return?" only applies to the
            manual Corporation-Tax-only flow. "Both" and "Company Accounts only"
            pull their dates straight from Companies House. */}
        {isCtOnly && (
          <div>
            <p className='mb-2 text-sm font-medium text-gray-900'>
              Is this your first Corporation Tax return?
            </p>
            <div className='flex gap-3'>
              {(
                [
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ] as const
              ).map(option => {
                const selected =
                  isFirstReturnAnswered && isFirstReturn === option.value
                return (
                  <button
                    key={option.label}
                    type='button'
                    onClick={() => handleFirstReturnSelect(option.value)}
                    className={`rounded-lg border-2 px-6 py-2 text-sm font-semibold transition-colors ${
                      selected
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
            {isFirstReturn && incorporationDate && (
              <p className='mt-2 text-xs text-gray-500'>
                We&apos;ve pre-filled the accounting period start with the
                incorporation date from Companies House. You can change it if
                needed.
              </p>
            )}
          </div>
        )}

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <DatePicker
              id='ct600-period-start'
              label='Accounting period start'
              value={setup.accountingPeriodStart}
              onChange={value =>
                onSetupChange({ accountingPeriodStart: value })
              }
              placeholder='dd/mm/yyyy'
              {...(setup.accountingPeriodEnd
                ? { max: setup.accountingPeriodEnd }
                : {})}
            />
            {validationErrors.periodStart && (
              <p className='mt-1 text-xs text-red-600'>
                {validationErrors.periodStart}
              </p>
            )}
          </div>
          <div>
            {(() => {
              const maxEnd = setup.accountingPeriodStart
                ? (() => {
                    const d = new Date(setup.accountingPeriodStart)
                    d.setMonth(d.getMonth() + 18)
                    return d.toISOString().split('T')[0]
                  })()
                : undefined
              const periodTooLong =
                setup.accountingPeriodStart && setup.accountingPeriodEnd
                  ? new Date(setup.accountingPeriodEnd).getTime() -
                      new Date(setup.accountingPeriodStart).getTime() >
                    18 * 30.4375 * 24 * 60 * 60 * 1000
                  : false
              return (
                <>
                  <DatePicker
                    id='ct600-period-end'
                    label='Accounting period end'
                    value={setup.accountingPeriodEnd}
                    onChange={value =>
                      onSetupChange({ accountingPeriodEnd: value })
                    }
                    placeholder='dd/mm/yyyy'
                    {...(setup.accountingPeriodStart
                      ? { min: setup.accountingPeriodStart }
                      : {})}
                    {...(maxEnd ? { max: maxEnd } : {})}
                  />
                  {periodTooLong && (
                    <p className='mt-1 text-xs text-red-600'>
                      Accounting period cannot exceed 18 months
                    </p>
                  )}
                  {!periodTooLong && validationErrors.periodEnd && (
                    <p className='mt-1 text-xs text-red-600'>
                      {validationErrors.periodEnd}
                    </p>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Period split / LPOA banner — shown for all three filing types so the
          user can always see whether the period is a standard 12 months or a
          long period of account requiring two CT600 returns. For ct-only the
          split is calculated from the manually-entered dates; for "both" and
          "company accounts only" it's pulled from Companies House. Shown once
          both dates are filled in (so there's something to summarise). */}
      {setup.accountingPeriodStart && setup.accountingPeriodEnd && (
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <h3 className='text-base font-semibold text-gray-900'>
              Accounting Period Summary
            </h3>
            <button
              type='button'
              onClick={isCtOnly ? onCalculateManualSplit : onRefreshPeriodSplit}
              disabled={isLoadingPeriodSplit}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                isPeriodConfirmed
                  ? 'border border-green-300 bg-green-50 text-green-700'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {isLoadingPeriodSplit ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <RefreshCw className='h-3 w-3' />
              )}
              {isPeriodConfirmed
                ? 'Accounting period confirmed'
                : 'Confirm accounting period'}
            </button>
          </div>

          {/* Show the "click Calculate split first" inline when the user tries
              to advance without confirming the manual period. */}
          {isCtOnly && validationErrors.periodSplit && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 p-3'>
              <p className='text-xs text-amber-800'>
                {validationErrors.periodSplit}
              </p>
            </div>
          )}

          {isLoadingPeriodSplit && !periodSplit && (
            <div className='flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4'>
              <Loader2 className='h-4 w-4 animate-spin text-gray-500' />
              <p className='text-sm text-gray-600'>
                {isCtOnly
                  ? 'Calculating period split from your dates…'
                  : "Looking up the company's period of account…"}
              </p>
            </div>
          )}

          {periodSplitError && !isLoadingPeriodSplit && (
            <div className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
              <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600' />
              <div className='text-sm text-amber-800'>
                <p className='font-medium'>
                  Couldn&apos;t auto-detect the period from Companies House.
                </p>
                <p className='mt-0.5 text-xs'>
                  {periodSplitError} You can still enter dates manually below.
                </p>
              </div>
            </div>
          )}

          {periodSplit && (
            <div
              className={`rounded-xl border p-5 ${
                periodSplit.isLpoa
                  ? 'border-amber-200 bg-amber-50'
                  : 'border-blue-200 bg-blue-50'
              }`}
            >
              <div className='flex items-start gap-3'>
                <Info
                  className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    periodSplit.isLpoa ? 'text-amber-600' : 'text-blue-600'
                  }`}
                />
                <div className='flex-1 min-w-0'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <p
                      className={`text-sm font-semibold ${
                        periodSplit.isLpoa ? 'text-amber-900' : 'text-blue-900'
                      }`}
                    >
                      {periodSplit.isLpoa
                        ? 'Long Period of Account — two CT600 returns required'
                        : 'Standard 12-month accounting period'}
                    </p>
                    <span className='inline-flex items-center rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 border border-gray-200'>
                      {periodSplit.derivedFrom === 'companies_house'
                        ? 'Pulled from Companies House'
                        : periodSplit.derivedFrom === 'override'
                          ? 'Custom dates'
                          : periodSplit.derivedFrom}
                    </span>
                  </div>
                  <p
                    className={`mt-1 text-xs ${
                      periodSplit.isLpoa ? 'text-amber-800' : 'text-blue-800'
                    }`}
                  >
                    Period of account:{' '}
                    <span className='font-medium'>
                      {formatPeriod(
                        periodSplit.periodOfAccount.start,
                        periodSplit.periodOfAccount.end
                      )}
                    </span>{' '}
                    ({periodSplit.periodOfAccount.days} days)
                  </p>

                  {/* Period pills */}
                  <div
                    className={`mt-3 flex flex-col gap-2 ${periodSplit.isLpoa ? '' : 'sm:flex-row'}`}
                  >
                    {periodSplit.periods.map(p => (
                      <div
                        key={p.index}
                        className='flex items-center gap-3 rounded-lg bg-white border border-gray-200 px-3 py-2'
                      >
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${
                            p.index === 1
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {p.index}
                        </span>
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs font-semibold text-gray-900 truncate'>
                            {p.label}
                          </p>
                          <p className='text-[11px] text-gray-500'>
                            {formatPeriod(p.start, p.end)} ({p.days} days)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {periodSplit.isLpoa && periodSplit.notes.length > 0 && (
                    <div className='mt-3'>
                      <button
                        type='button'
                        onClick={() => setShowLpoaNotes(v => !v)}
                        className='inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:text-amber-700'
                      >
                        {showLpoaNotes ? (
                          <ChevronDown className='h-3 w-3' />
                        ) : (
                          <ChevronRight className='h-3 w-3' />
                        )}
                        How LPOA filing works
                      </button>
                      {showLpoaNotes && (
                        <ul className='mt-2 space-y-1 text-xs text-amber-800'>
                          {periodSplit.notes
                            .filter(
                              n =>
                                !/box\s*8[05]|submitted with separate return/i.test(
                                  n
                                )
                            )
                            .map((n, i) => (
                              <li key={i} className='flex items-start gap-2'>
                                <span className='mt-1.5 h-1 w-1 rounded-full bg-amber-600 flex-shrink-0' />
                                <span>{n}</span>
                              </li>
                            ))}
                        </ul>
                      )}
                      <p className='mt-2 text-[11px] text-amber-700'>
                        You&apos;ll still file once — Britfiler handles the AP1
                        and AP2 envelopes for you.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Company Flags — moved from step 2 so the user picks dormant/close/
          investment-company before choosing a plan. Toggling Dormant opens
          the plan modal filtered to dormant-only plans. */}
      <div className='space-y-4'>
        <h3 className='text-base font-semibold text-gray-900'>Company Flags</h3>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
          {(
            [
              {
                key: 'isDormant',
                label: 'Dormant',
                desc: 'No significant accounting transactions during the period.',
              },
              {
                key: 'isCloseCompany',
                label: 'Close Company',
                desc: 'Controlled by 5 or fewer participators.',
              },
              {
                key: 'isInvestmentCompany',
                label: 'Investment Company',
                desc: 'Business consists wholly or mainly of investments.',
              },
            ] as const
          ).map(({ key, label, desc }) => {
            const checked = companyFlags[key]
            return (
              <label
                key={key}
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-colors ${
                  checked
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type='checkbox'
                  checked={checked}
                  onChange={e =>
                    onCompanyFlagsChange({
                      ...companyFlags,
                      [key]: e.target.checked,
                    })
                  }
                  className='h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
                />
                <span className='text-sm font-semibold text-gray-900'>
                  {label}
                </span>
                <p className='text-xs text-gray-500 leading-snug'>{desc}</p>
              </label>
            )
          })}
        </div>
      </div>

      {/* Plan Selection */}
      <div className='space-y-4'>
        <h3 className='text-base font-semibold text-gray-900'>Pricing Plan</h3>
        <div className='flex items-center gap-4'>
          <button
            type='button'
            onClick={onOpenPlanModal}
            className='rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors'
          >
            {selectedPlanSlug ? 'Change Plan' : 'Select a Plan'}
          </button>
          {selectedPlanSlug && (
            <span className='rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700'>
              Plan selected: {selectedPlanSlug}
            </span>
          )}
        </div>
        {validationErrors.planSlug && (
          <p className='text-xs text-red-600'>{validationErrors.planSlug}</p>
        )}
      </div>
    </div>
  )
}
